import React, { useCallback, useEffect, useMemo } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import i18n from '../../i18n';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import MainPageState from '../main/main-page-states';
import { MenuStoreState } from './menu-store-state';
import { ReactComponent as BackIcon } from '../../static/images/svg/typcn_arrow-back.svg';
import { ReactComponent as BxsStoreIcon } from '../../static/images/svg/bxs_store.svg';
import SearchInput from '../../component/form/search-input.component';
import { MenuStoreBrowseGrid } from './menu-store-browse-grid.component';
import { MenuStoreDetailView } from './menu-store-detail-view.component';
import { MenuStoreDishDetailDialog } from './menu-store-dish-detail-dialog.component';
import { MenuStoreUseNowResultDialog } from './menu-store-use-now-result-dialog.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { CustomerLoading } from '../../component/loading/loading';
import type { MenuStoreMenuListItem } from '../../models/menu-store.entity';
/** 商店首页 / 我的菜单列表与页壳（与详情样式分离引入） */
import './scss/menu-store-page/menu-store.page.layout-list.scss';

/**
 * 菜单商店：主页面 + 我的菜单独立页（已激活/我上传）+ 详情 + 激活/立刻使用弹窗
 */
const MenuStorePage: React.FC = observer(() => {
    /** 页面状态（菜单商店） */
    const state = useLocalObservable(() => new MenuStoreState());
    /** 顶栏状态 */
    const topBarState = MobxObjectsManager.get(TopBarState);
    /** 主页面全局状态（当前餐厅、归属门店等） */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    /** 当前餐厅 string_id（激活弹窗默认目标） */
    const restaurantId = mainPageData.currentRestaurant?.string_id;
    /** 可选门店列表（用于「立刻使用」） */
    const useNowStoreOptions = useMemo(
        () =>
            (mainPageData.belongedRestaurant ?? [])
                .map((r) => ({
                    value: r.string_id || '',
                    label: (r.nickname || r.string_id || '').trim() || '—',
                }))
                .filter((o) => o.value !== ''),
        [mainPageData.belongedRestaurant],
    );

    useEffect(() => {
        topBarState.updateTitle('menu_store_page_text');
        topBarState.updateSubTitle('');
    }, []);

    useEffect(() => {
        void state.openStoreHome();
    }, []);

    useEffect(() => {
        if (state.rootView === 'my-menus') {
            topBarState.updateTitle('menu_store_tab_my_menus');
            return;
        }
        topBarState.updateTitle('menu_store_page_text');
    }, [state.rootView]);

    /** 首页卡片数据 */
    const storeHomeList = state.browseList;
    /** 首页加载态 */
    const storeHomeLoading = state.browseLoading;
    /** 我的菜单当前 Tab 列表（可按搜索词前端过滤） */
    const myMenusCurrentList = (state.myMenusTab === 'activated' ? state.activatedMenus : state.uploadedMenus).filter((item) => {
        const keyword = state.browseSearchName.trim().toLowerCase();
        if (!keyword) {
            return true;
        }
        return item.name.toLowerCase().includes(keyword);
    });
    /** 我的菜单加载态 */
    const myMenusLoading = state.myMenusLoading;
    /** 商店首页 / 我的菜单列表是否在拉取数据（用于全屏遮罩占位，避免绝对定位 loading 父级高度为 0） */
    const listLoading = state.rootView === 'store-home' ? storeHomeLoading : myMenusLoading;

    /** 授权码管理：拉取列表 */
    const refreshLicenseCodesForMenu = useCallback((menuId: string) => {
        void state.fetchMenuStoreLicenseCodes(menuId);
    }, [state]);

    /** 生成授权码：提交接口 */
    const requestGenerateLicenses = useCallback((menuId: string) => {
        void state.postMenuStoreGenerateLicenseCodes(menuId);
    }, [state]);

    /** 商店首页虚拟列表触底加载（稳定引用，避免 Virtuoso 重置滚动） */
    const loadBrowseStoreMore = useCallback(() => {
        void state.loadBrowseListMore();
    }, [state]);

    /** 我的菜单当前 Tab 触底加载下一页 */
    const loadMyMenusStoreMore = useCallback(() => {
        if (state.myMenusTab === 'activated') {
            void state.loadMyMenusPurchasedMore();
        } else {
            void state.loadMyMenusUploadedMore();
        }
    }, [state]);

    /** 列表卡片打开详情（商店首页与我的菜单共用，稳定引用） */
    const openMenuStoreCardDetail = useCallback((item: MenuStoreMenuListItem) => {
        state.openDetail(item);
    }, [state]);

    /** 我的菜单虚拟列表：Tab + 已提交搜索词变化时重置滚动位置 */
    const myMenusVirtuosoResetKey = `${state.myMenusTab}\u0000${state.browseSearchName}`;

    return (
        <Box className="menu-store-page">
            {state.viewMode === 'list' ? (
                <Stack spacing={0} className="menu-store-page__list-shell">
                    {state.rootView === 'store-home' ? (
                        <Box className="menu-store-page__toolbar">
                            <Box className="menu-store-page__toolbar-left">
                                <Box className="menu-store-page__search-pill">
                                    <SearchInput
                                        className="menu-store-page__search-field"
                                        allowClear
                                        placeholder={i18n.t('menu_store_search_placeholder')}
                                        initialValue={state.browseSearchName}
                                        onSearch={(v) => state.applyStoreBrowseSearch(v)}
                                    />
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                className="menu-store-page__btn-my-menus"
                                startIcon={<BxsStoreIcon className="menu-store-page__btn-my-menus-icon" aria-hidden />}
                                onClick={() => void state.openMyMenus()}
                            >
                                {i18n.t('menu_store_tab_my_menus')}
                            </Button>
                        </Box>
                    ) : (
                        <Box className="menu-store-page__my-layout-head">
                            <Box className="menu-store-page__my-title-pill">
                                <BxsStoreIcon className="menu-store-page__my-title-icon" aria-hidden />
                                <Typography variant="h4" component="span" className="menu-store-page__my-title-text">
                                    {i18n.t('menu_store_tab_my_menus')}
                                </Typography>
                            </Box>
                            <Box className="menu-store-page__my-layout-right">
                                <Box className="menu-store-page__search-pill">
                                    <SearchInput
                                        className="menu-store-page__search-field"
                                        allowClear
                                        placeholder={i18n.t('menu_store_search_placeholder')}
                                        initialValue={state.browseSearchName}
                                        onSearch={(v) => state.applyMyMenusSearchFilter(v)}
                                    />
                                </Box>
                                <Button
                                    className="menu-store-page__btn-back-home"
                                    startIcon={<BackIcon />}
                                    onClick={() => void state.openStoreHome()}
                                >
                                    {i18n.t('menu_store_btn_back_home')}
                                </Button>
                            </Box>
                            <Tabs
                                className="menu-store-page__tabs"
                                value={state.myMenusTab}
                                onChange={(_e, v) => state.setMyMenusTab(v)}
                                aria-label={i18n.t('menu_store_tabs_my_menus_aria')}
                            >
                                <Tab value="activated" label={i18n.t('menu_store_tab_activated_menus')} />
                                <Tab value="uploaded" label={i18n.t('menu_store_tab_uploaded_menus')} />
                            </Tabs>
                        </Box>
                    )}

                    <Box
                        className={[
                            'menu-store-page__grid-container',
                            listLoading ? 'menu-store-page__grid-container--loading' : '',
                            !listLoading &&
                            ((state.rootView === 'store-home' && storeHomeList.length > 0) ||
                                (state.rootView === 'my-menus' && myMenusCurrentList.length > 0))
                                ? 'menu-store-page__grid-container--store-browse-virtuoso'
                                : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        role={listLoading ? 'status' : undefined}
                        aria-live={listLoading ? 'polite' : undefined}
                        aria-label={listLoading ? i18n.t('general_text_loading') : undefined}
                    >
                        {listLoading ? (
                            <CustomerLoading />
                        ) : (state.rootView === 'store-home' ? storeHomeList : myMenusCurrentList).length === 0 ? (
                            <Box className="menu-store-page__state-msg">
                                <Typography variant="body1">{i18n.t('general_text_no_or_more_data')}</Typography>
                            </Box>
                        ) : state.rootView === 'store-home' ? (
                            <MenuStoreBrowseGrid
                                listResetKey={state.browseSearchName}
                                items={storeHomeList}
                                loadingMore={state.browseLoadingMore}
                                onLoadMore={loadBrowseStoreMore}
                                onOpenItem={openMenuStoreCardDetail}
                            />
                        ) : (
                            <MenuStoreBrowseGrid
                                listResetKey={myMenusVirtuosoResetKey}
                                items={myMenusCurrentList}
                                loadingMore={
                                    state.myMenusTab === 'activated'
                                        ? state.myMenusPurchasedLoadingMore
                                        : state.myMenusUploadedLoadingMore
                                }
                                onLoadMore={loadMyMenusStoreMore}
                                onOpenItem={openMenuStoreCardDetail}
                            />
                        )}
                    </Box>
                </Stack>
            ) : (
                <Stack spacing={0} className={['menu-store-page__detail-shell', state.rootView === 'my-menus' ? 'menu-store-page__detail-shell--my-menus' : ''].filter(Boolean).join(' ')}>
                    {state.rootView === 'my-menus' ? (
                        <Box className="menu-store-page__my-layout-head">
                            <Box className="menu-store-page__my-title-pill">
                                <BxsStoreIcon className="menu-store-page__my-title-icon" aria-hidden />
                                <Typography variant="h4" component="span" className="menu-store-page__my-title-text">
                                    {i18n.t('menu_store_tab_my_menus')}
                                </Typography>
                            </Box>
                            <Box className="menu-store-page__my-layout-right">
                                <Box className="menu-store-page__search-pill">
                                    <SearchInput
                                        className="menu-store-page__search-field"
                                        allowClear
                                        placeholder={i18n.t('menu_store_search_placeholder')}
                                        initialValue={state.browseSearchName}
                                        onSearch={(v) => state.applyMyMenusSearchFilter(v)}
                                    />
                                </Box>
                                <Button
                                    className="menu-store-page__btn-back-home"
                                    startIcon={<BackIcon />}
                                    onClick={() => void state.openStoreHome()}
                                >
                                    {i18n.t('menu_store_btn_back_home')}
                                </Button>
                            </Box>
                            <Tabs
                                className="menu-store-page__tabs"
                                value={state.myMenusTab}
                                onChange={(_e, v) => state.setMyMenusTab(v)}
                                aria-label={i18n.t('menu_store_tabs_my_menus_aria')}
                            >
                                <Tab value="activated" label={i18n.t('menu_store_tab_activated_menus')} />
                                <Tab value="uploaded" label={i18n.t('menu_store_tab_uploaded_menus')} />
                            </Tabs>
                        </Box>
                    ) : null}
                    <MenuStoreDetailView
                        item={state.selectedMenu}
                        detailSource={state.resolvedDetailSource}
                        onBack={() => state.backToList()}
                        hideTopBack={state.rootView === 'my-menus'}
                        showStoreBrowseToolbar={state.rootView === 'store-home'}
                        storeBrowseSearchInitial={state.browseSearchName}
                        onStoreBrowseSearch={(v) => state.applyStoreBrowseSearch(v)}
                        onOpenMyMenus={() => void state.openMyMenus()}
                        onActivate={() => state.openActivateDialog()}
                        onSubmitActivate={() => void state.submitActivate()}
                        activateOpen={state.activateDialogOpen}
                        activateLicense={state.activateLicenseInput}
                        onActivateLicenseChange={(v) => state.setActivateLicenseInput(v)}
                        onCloseActivate={() => state.closeActivateDialog()}
                        activateSubmitting={state.activateSubmitting}
                        useNowOpen={state.useNowDialogOpen}
                        useNowStoreIds={state.useNowRestaurantStringIds}
                        onOpenUseNow={() => state.openUseNowDialog()}
                        onCloseUseNow={() => state.closeUseNowDialog()}
                        onToggleUseNowStore={(v) => state.toggleUseNowRestaurantStringId(v)}
                        onSubmitUseNow={() => void state.submitUseNow(state.selectedMenu, useNowStoreOptions)}
                        useNowSubmitting={state.useNowSubmitting}
                        useNowStoreOptions={useNowStoreOptions}
                        onOpenDishDetail={(dish) => state.openDishDetail(dish)}
                        licenseCodeRows={state.licenseCodeRows}
                        licenseCodesLoading={state.licenseCodesLoading}
                        onRefreshLicenseCodesForMenu={refreshLicenseCodesForMenu}
                        generatedLicenseCodes={state.displayGeneratedLicenseCodes}
                        generateLicenseSubmitting={state.generateLicenseSubmitting}
                        onRequestGenerateLicenses={requestGenerateLicenses}
                        canRemoveMenu={state.selectedMenu != null && state.canRemoveMenu(state.selectedMenu)}
                        onRemoveMenu={() => state.openRemoveMenuConfirm()}
                        removeMenuSubmitting={state.removingMenuId === state.selectedMenu?._id}
                        previewSaveSubmitting={state.previewSaveSubmitting}
                        onSavePreviewEdit={(maskMap) => state.saveMenuStoreDishVisibility(state.selectedMenu, maskMap)}
                        menuDetailLoading={state.menuDetailLoading}
                    />
                    <MenuStoreDishDetailDialog
                        open={state.viewMode === 'dish-detail'}
                        dish={state.selectedDish}
                        onClose={() => state.backToMenuDetail()}
                        restaurantStringId={restaurantId}
                        detailSource={state.resolvedDetailSource}
                    />
                    <MenuStoreUseNowResultDialog
                        open={state.useNowResultDialogOpen}
                        onClose={() => state.closeUseNowResultDialog()}
                        subtitle={state.useNowResultSubtitle}
                        lines={state.useNowResultDialogLines}
                        hadSuccessfulSync={state.useNowResultHadSuccessfulSync}
                    />
                    <AlertDialog
                        open={state.removeMenuConfirmOpen}
                        variant="delete"
                        size="sm"
                        title={i18n.t('menu_store_btn_remove_menu')}
                        bodyPrompt={i18n.t('menu_store_remove_menu_confirm_prompt')}
                        highlightText={(state.selectedMenu?.name ?? '').trim() || '—'}
                        confirmText={i18n.t('menu_store_btn_remove_menu')}
                        onCancel={() => state.closeRemoveMenuConfirm()}
                        onClose={() => state.closeRemoveMenuConfirm()}
                        onConfirm={() => {
                            const m = state.selectedMenu;
                            state.closeRemoveMenuConfirm();
                            if (m) {
                                void state.removeMenuFromStore(m);
                            }
                        }}
                    />
                </Stack>
            )}
        </Box>
    );
});

export default MenuStorePage;
